import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'

export interface ShareLink {
  id: string
  sections: string[]
  expires_at: string
  created_at: string
  is_expired: boolean
}

export interface ShareLinkCreated extends ShareLink {
  token: string
  url: string
}

export interface CreateShareLinkPayload {
  sections: string[]
  expires_in_days: 7 | 30 | 90
}

export function useShareLinks() {
  return useQuery<ShareLink[]>({
    queryKey: ['share-links'],
    queryFn: () => api.get<ShareLink[]>('/api/v1/sharing/links'),
    staleTime: 30_000,
  })
}

export function useCreateShareLink() {
  const qc = useQueryClient()
  return useMutation<ShareLinkCreated, Error, CreateShareLinkPayload>({
    mutationFn: (payload) =>
      api.post<ShareLinkCreated>('/api/v1/sharing/links', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['share-links'] }),
  })
}

export function useRevokeShareLink() {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (linkId) =>
      api.delete<void>(`/api/v1/sharing/links/${linkId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['share-links'] }),
  })
}

export interface SharedDashboardData {
  link_id: string
  sections: string[]
  expires_at: string
  data: {
    executive?: Record<string, unknown>
    revenue?: Record<string, unknown>
    margin?: Record<string, unknown>
    billing?: Record<string, unknown>
    recurring?: Record<string, unknown>
  }
}

import { API_BASE } from '../lib/api'

export async function fetchSharedData(token: string): Promise<SharedDashboardData> {
  const resp = await fetch(`${API_BASE}/api/v1/sharing/${token}/data`)
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}))
    throw Object.assign(new Error(body.detail ?? 'Errore caricamento dashboard condivisa'), {
      status: resp.status,
    })
  }
  return resp.json()
}
